import { caseTypeLabel } from './caseTypes';
import { listingPlace } from './listingPlace';
import { getCoverageShift } from './coverageShift';
/**
 * Utility functions for transforming listing data for consistent display across the application
 */

/**
 * Build the honest market-band tooltip copy for the unified "Competitive Rate"
 * badge shown on any listing whose rate is not displayed (comp-blind or blind).
 *
 * The band is drawn from real comparables in the same state / provider type /
 * assignment segment. It is NEVER this listing's own offer, so the copy frames
 * it explicitly as the market range for the segment. Dollars round to whole
 * numbers. When the resolved band is not category-specific (or there is no
 * canonical category), the assignment word is omitted so we never overclaim a
 * category-specific range.
 *
 * @param {object} args
 * @param {string} args.providerType - e.g. "CRNA", "Anesthesiologist", "CAA"
 * @param {string} args.state - two-letter state code, e.g. "FL"
 * @param {string|null} args.canonicalCategory - "locum" | "per_diem" | "part_time" | "full_time" | null
 * @param {{p25:number, median:number, p75:number, n:number, tier:string}|null} args.band
 * @returns {string|null} tooltip copy, or null when there is no band
 */
export const buildMarketBandTooltip = ({ providerType, state, canonicalCategory, band } = {}) => {
  if (!band) return null;

  // Backward compatible: a legacy flat band (top-level p25) is the hourly band.
  const hourly = band.hourly ?? (typeof band.p25 === 'number' ? band : null);
  const annual = band.annual ?? null;
  if (!hourly && !annual) return null;

  const type = providerType || 'anesthesia';
  const loc = state || 'your area';

  // Only name the assignment when a band resolved at the category-specific tier,
  // otherwise the range is not category-specific.
  const tierCell = hourly || annual;
  const isCategoryTier = tierCell.tier === 'state_type_category';
  const assignment =
    isCategoryTier && canonicalCategory
      ? `${String(canonicalCategory).replace(/_/g, ' ')} `
      : '';

  const parts = [];
  if (hourly) {
    parts.push(
      `$${Math.round(hourly.p25)} to $${Math.round(hourly.p75)}/hr (median $${Math.round(hourly.median)})`,
    );
  }
  if (annual) {
    parts.push(
      `salary $${Math.round(annual.p25 / 1000)}k to $${Math.round(annual.p75 / 1000)}k/yr (median $${Math.round(annual.median / 1000)}k)`,
    );
  }

  return (
    `Market range for ${type} ${assignment}roles in ${loc}: ` +
    `${parts.join('; ')}. ` +
    "This is the market, not this listing's confirmed rate."
  );
};

/**
 * Comp-blind rate capture (Phase 2): the provider-side Pay snapshot for a
 * comp-blind listing in the negotiation view.
 *
 * This is the ONLY provider-facing change in Phase 2 and it is deliberately
 * confined to the comp-blind path: it returns null for every non-comp-blind
 * listing (real-offer AND internal-blind), so the existing real-offer provider
 * UX is completely untouched. When a comp-blind listing's rate later lands, the
 * backend flips `compensation.isCompBlind` false and populates the real rate, so
 * this helper returns null again and the normal rate display takes over.
 *
 * Reuses the Phase 1 "Competitive Rate" label (no new provider primitive) and
 * adds only a small awaiting-rate note while the recruiter has been asked for
 * the rate but has not yet submitted it.
 *
 * @param {object|null} compensation - the transformed listing compensation
 * @param {object} [opts]
 * @param {boolean} [opts.awaitingCompensation] - the interested swipe is flagged
 *   awaiting the recruiter's rate (backend `swipe.awaitingCompensation`).
 * @returns {{ value: string, note: string|null }|null} snapshot, or null when
 *   this is not a comp-blind listing.
 */
export const getCompBlindPaySnapshot = (compensation, opts = {}) => {
  if (!compensation || !compensation.isCompBlind) return null;
  return {
    // Reuse the Phase 1 unified label; never invent a new provider primitive.
    value: 'Competitive Rate',
    note: opts.awaitingCompensation
      ? 'Rate requested from the recruiter.'
      : null,
  };
};

/**
 * Format case type string to proper display label
 * @param {string} caseType - The raw case type string (e.g., "pediatric", "critical-care")
 * @returns {string} Formatted case type label (e.g., "Pediatric", "Critical Care")
 */
/**
 * A stored case type, in the words to show a human.
 *
 * THE FALLBACK WAS THE BUG. This knew eight lowercase slugs and title-cased
 * anything else by splitting on '-' and upper-casing the first letter of each
 * word -- which is wrong for every initialism in the vocabulary. The stored
 * 'OB' rendered as "Ob" on 54,511 listings and 'GI' as "Gi" on 18,217, on every
 * card that shows a case type.
 *
 * Delegated to the shared vocabulary, which knows all three dialects and
 * carries authored labels rather than deriving them. A value it does not
 * recognise still comes back as typed, which is the same conservatism: showing
 * a recruiter their own unusual word beats showing them a mangled one.
 */
export const formatCaseType = (caseType) => caseTypeLabel(caseType);

/**
 * Format facility type string to proper case (e.g., "primary_care" -> "Primary Care")
 * @param {string} facilityType - The raw facility type string
 * @returns {string} Formatted facility type string
 */
export const formatFacilityTypeString = (facilityType) => {
  if (!facilityType) return '';

  // Replace underscores with spaces and capitalize each word
  return facilityType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Convert any facility type to a backend-compatible value
 * @param {string} type - The input facility type
 * @returns {string} Backend-compatible facility type
 */
export const normalizeBackendFacilityType = (type) => {
  // A provider profile carries facilityType as a list of preferences ("I would
  // work in a hospital or a surgery center"), and the || chains in
  // transformListingData hand whatever they find straight through. An array is
  // truthy, so it used to sail past the guard below and die on .toLowerCase(),
  // taking the whole /demo/crna card down with it. First preference wins:
  // every caller renders this into a single display slot.
  const raw = Array.isArray(type) ? type[0] : type;
  if (typeof raw !== 'string' || !raw) return 'hospital'; // Default

  // Convert to lowercase and check if it's in our mapping
  const lowercaseType = raw.toLowerCase();
  // Return mapped value or the original if no mapping exists
  return facilityTypeMap[lowercaseType] || lowercaseType;
};

/**
 * Get user-friendly display name for a facility type
 * @param {string} type - The raw facility type
 * @returns {string} User-friendly display name
 */
export const getFacilityTypeDisplayName = (type) => {
  if (!type) return 'Hospital'; // Default

  // Try direct mapping
  const normalizedType = normalizeBackendFacilityType(type);

  // Either use the display mapping or format the normalized type
  return facilityTypeDisplayMap[normalizedType] || formatFacilityTypeString(normalizedType) || 'Hospital';
};

// Map display facility types to backend-compatible values and vice versa
// Updated to match the new backend enum values
export const facilityTypeMap = {
  // Frontend display to backend value
  'Hospital': 'hospital',
  'Ambulatory Surgery Center': 'ambulatory_surgery_center',
  'Surgery Center': 'ambulatory_surgery_center',
  'Office-Based': 'office_based_practice',
  'Office-Based Practice': 'office_based_practice',
  'Pain Clinic': 'pain_management_clinic',
  'Pain Management': 'pain_management_clinic',
  'Emergency Department': 'emergency_department',
  'Emergency': 'emergency_department',
  'Obstetrics': 'obstetrics_birthing',
  'Birthing Center': 'obstetrics_birthing',
  'Other': 'other',
};

// Map backend values to user-friendly display labels
export const facilityTypeDisplayMap = {
  'hospital': 'Hospital',
  'ambulatory_surgery_center': 'Ambulatory Surgery Center',
  'office_based_practice': 'Office-Based Practice',
  'pain_management_clinic': 'Pain Management Clinic',
  'emergency_department': 'Emergency Department',
  'obstetrics_birthing': 'Obstetrics / Birthing',
  'other': 'Other',
};

/**
 * Transform listing data for display in FlippableListingCard
 * @param {Object} data - The data object which can be a listing, match, or facility
 * @param {Number} yearsOfExperience - Optional provider years of experience to map to a specific tier
 * @returns {Object} Transformed listing data
 */
export const transformListingData = (data, yearsOfExperience = null) => {
  // Extract the listing data based on the structure provided.
  // New API returns `listing` directly on the match object (snapshot data).
  // Fall back to `listingId` for backward compatibility, or treat the input
  // object itself as a listing if it already contains facility information.
  const listing = data.listing
    ? data.listing
    : (data.listingId ? data.listingId : (data.facility ? data : data));
  const facility = listing.facility || {};

  // Pay and contract initial extraction
  let payAmount = listing.compensation?.amount || listing.compensation?.hourlyAmount || listing.compensation?.annualAmount || listing.payAmount || listing.payRate || listing.minIncome || 0;
  let payAmountMax = listing.compensation?.maxHourlyAmount || listing.compensation?.maxAnnualAmount || listing.maxIncome || 0;
  let payPeriod = listing.compensation?.period || listing.payPeriod || listing.period ||
    (listing.compensation?.hourlyAmount ? 'hourly' :
     listing.compensation?.annualAmount ? 'yearly' :
     (listing.isHourlyRate ? 'hourly' : (listing.minIncome ? 'yearly' : 'hourly')));

  // Extract tiered compensation if base values are missing (or 0) but tiers are provided
  if (!payAmount && listing.compensation?.tiers?.length > 0) {
    let applicableTiers = listing.compensation.tiers;
    
    // Narrow down to the single matching tier if we know the provider's experience
    if (yearsOfExperience !== null && yearsOfExperience !== undefined) {
        const matchedTiers = applicableTiers.filter(t => 
            (t.minYearsExperience == null || yearsOfExperience >= t.minYearsExperience) &&
            (t.maxYearsExperience == null || yearsOfExperience <= t.maxYearsExperience)
        );
        if (matchedTiers.length > 0) {
            applicableTiers = matchedTiers;
        }
    }
    
    const maxTierHourly = Math.max(...applicableTiers.map(t => t.hourlyMax || 0));
    const minTierHourly = Math.min(...applicableTiers.filter(t => t.hourlyMin || t.hourlyMax).map(t => t.hourlyMin || t.hourlyMax));
    
    const maxTierAnnual = Math.max(...applicableTiers.map(t => t.annualMax || 0));
    const minTierAnnual = Math.min(...applicableTiers.filter(t => t.annualMin || t.annualMax).map(t => t.annualMin || t.annualMax));
    
    if (maxTierHourly > 0) {
        payAmount = minTierHourly !== Infinity ? minTierHourly : maxTierHourly;
        payAmountMax = maxTierHourly;
        payPeriod = 'hourly';
    } else if (maxTierAnnual > 0) {
        payAmount = minTierAnnual !== Infinity ? minTierAnnual : maxTierAnnual;
        payAmountMax = maxTierAnnual;
        payPeriod = 'yearly';
    }
  }

  // Create a properly structured profile object that matches what FlippableListingCard expects
  return {
    // Basic info
    _id: listing._id || '',
    id: listing.id || listing._id || '',
    title: listing.title || '',
    // Server-owned provider-facing identity. This is deliberately distinct
    // from facilityName, whose legacy display transform appends a facility
    // type and therefore mangles account brands/custom labels.
    publicDisplayName: listing.publicDisplayName || '',
    // Use facility name and append normalized facility type if available
    facilityName: (facility.name || listing.facilityName || 'Hospital') +
      ((facility.facilityType || listing.facilityType || facility.type || listing.type) ?
        ` - ${getFacilityTypeDisplayName(facility.facilityType || listing.facilityType || facility.type || listing.type)}` :
        ''),
    // filter(Boolean).join, not a template with .trim(): trim strips outer
    // whitespace but leaves the separator, so a city with no state rendered as
    // "Philadelphia," on the card. Invisible on saved listings (which always
    // have both) and obvious the moment the employers card builder started
    // repainting mid-keystroke. Identical output when both parts are present.
    // Every branch goes through listingPlace, which owns the join: it drops a
    // bare separator (a city with no state read as "Philadelphia,"), a city
    // that is its own state ("MI" in MI, which read as "MI, MI"), and the
    // 'Unknown' placeholder ingestion writes into a required column.
    location: (facility.address?.city
      ? listingPlace(facility.address.city, facility.address.state).location
      : '') ||
      facility.location ||
      facility.facilityDetails?.location ||
      // Check for state field on listing (common for internal listings)
      listingPlace(listing.city, listing.state).location ||
      // Check for city/state on facility details
      listingPlace(facility.facilityDetails?.city, facility.facilityDetails?.state).location ||
      listing.location ||
      'Location TBD',
    // Pass through city and state separately for ExternalListingFront
    // Parse city from facility.location if not available directly (format: "City, State")
    city: listing.city || facility.address?.city || facility.facilityDetails?.city ||
      (facility.location?.includes(',') ? facility.location.split(',')[0].trim() : '') || '',
    state: listing.state || facility.address?.state || facility.facilityDetails?.state ||
      (facility.location?.includes(',') ? facility.location.split(',')[1]?.trim() : '') || '',
    groupName: facility.groupName || listing.groupName || '',

    // Facility details - properly formatted
    facilityType: getFacilityTypeDisplayName(facility.facilityType || listing.facilityType || facility.type || facility.facilityDetails?.facilityType || ''),

    // Pay and contract - use the computed values from above handling regular and tiered compensation
    payAmount: payAmount,
    payAmountMax: payAmountMax,
    payPeriod: payPeriod,
    // Unknown employment type stays null so the display hides the Type chip
    // rather than asserting a wrong concrete value (e.g. mislabeling a 1099
    // locum as "W2"). Do NOT default this to a literal type.
    contractType: listing.compensation?.contractType || listing.contractType || null,

    // Compensation object - needed for FlippableListingCard front and back
    compensation: {
      type: listing.compensation?.type || 'Hourly',
      amount: listing.compensation?.amount || listing.payAmount || listing.payRate || 0,
      period: listing.compensation?.period || listing.payPeriod || listing.period || 'hourly',
      hourlyAmount: listing.compensation?.hourlyAmount || 0,
      maxHourlyAmount: listing.compensation?.maxHourlyAmount || 0,
      annualAmount: listing.compensation?.annualAmount || 0,
      maxAnnualAmount: listing.compensation?.maxAnnualAmount || 0,
      // Weekly-quoted locum: pass the verbatim posted weekly figure through so the
      // card renders "$X.X-Y.YK/week" instead of the annualized (×52) value.
      estimatedFromPeriod: listing.compensation?.estimatedFromPeriod || null,
      postedAmount: listing.compensation?.postedAmount || 0,
      postedMaxAmount: listing.compensation?.postedMaxAmount || 0,
      // See note above: unknown type stays null so the Type chip is hidden.
      contractType: listing.compensation?.contractType || listing.contractType || null,
      includesHousing: listing.compensation?.includesHousing ?? listing.includesHousing ?? null,
      includesTravel: listing.compensation?.includesTravel ?? listing.includesTravel ?? null,
      malpracticeCovered: listing.compensation?.malpracticeCovered ?? listing.malpracticeCovered ?? null,
      signOnBonusOffered: listing.compensation?.signOnBonusOffered || false,
      signOnBonusAmount: listing.compensation?.signOnBonusAmount || 0,
      signOnBonusCommitmentYears: listing.compensation?.signOnBonusCommitmentYears || 0,
      // Blind listing flag — controls whether comp numbers are shown to providers
      isBlind: listing.compensation?.isBlind || false,
      // Comp-blind flag — the listing never carried a rate. Drives the unified
      // "Competitive Rate" badge (with a market-band tooltip) the same way isBlind does.
      isCompBlind: listing.compensation?.isCompBlind || false,
      // Experience tiers — array of { minYearsExperience, maxYearsExperience, hourlyMin, hourlyMax }
      tiers: listing.compensation?.tiers || [],
    },

    // Schedule - support both flat and nested structures
    scheduleType: listing.scheduleType || listing.schedule?.pattern || '',
    // THE OTHER SCHEDULE AXIS. `pattern` answers which DAYS; this answers the
    // shape of the week ('3x12', '4x10'). They were one field, which is why a
    // listing saying "Three 12s" used to render its shape in the day row and
    // could not be edited by the day control.
    shiftStructure: listing.shiftStructure || listing.schedule?.shiftStructure || '',
    estimatedWeeklyHours: listing.estimatedWeeklyHours || listing.schedule?.estimatedWeeklyHours || 0,
    onCall: listing.onCall || listing.schedule?.onCall || false,
    weekends: listing.weekends || listing.schedule?.weekends || false,

    // Create schedule object for structured access
    schedule: {
      coverageShift: getCoverageShift(listing),
      shiftTimes: listing.schedule?.shiftTimes || (listing.vacancyReference ? listing.shift : '') || '',
      pattern: listing.scheduleType || listing.schedule?.pattern || '',
      shiftStructure: listing.shiftStructure || listing.schedule?.shiftStructure || '',
      estimatedWeeklyHours: listing.estimatedWeeklyHours || listing.schedule?.estimatedWeeklyHours || 0,
      onCall: listing.onCall || listing.schedule?.onCall || false,
      weekends: listing.weekends || listing.schedule?.weekends || false
    },

    // Assignment - ensure proper nesting and array structure
    assignmentType: Array.isArray(listing.assignment?.types) ? listing.assignment.types :
      (listing.assignment?.type ? [listing.assignment.type] :
        (listing.assignmentType ? [listing.assignmentType] : [])),
    startDate: listing.assignment?.startDate || listing.startDate || (listing.vacancyReference ? listing.date : '') || '',
    endDate: listing.assignment?.endDate || listing.endDate || '',

    // Create assignment object for structured access
    assignment: {
      types: Array.isArray(listing.assignment?.types) ? listing.assignment.types :
        (listing.assignment?.type ? [listing.assignment.type] :
          (listing.assignmentType ? [listing.assignmentType] : [])),
      startDate: listing.assignment?.startDate || listing.startDate || (listing.vacancyReference ? listing.date : '') || '',
      duration: listing.assignment?.duration || listing.duration || '',
      // Canonical assignment category (locum | per_diem | part_time | full_time)
      // Used to caption the market-band tooltip for comp-blind listings.
      canonicalCategory: listing.assignment?.canonicalCategory || null
    },

    // Clinical expectations - ensure proper structure for the back of the card
    clinicalExpectations: {
      requiredCaseTypes: Array.isArray(listing.clinical?.requiredCaseTypes) ? listing.clinical.requiredCaseTypes :
        Array.isArray(listing.clinicalRequirements?.requiredCaseTypes) ? listing.clinicalRequirements.requiredCaseTypes :
          Array.isArray(listing.clinicalExpectations?.requiredCaseTypes) ? listing.clinicalExpectations.requiredCaseTypes :
            Array.isArray(listing.clinical?.caseTypes) ? listing.clinical.caseTypes : [],
      // Fix EMR system mapping - prioritize facility.clinicalExpectations.emrSystem as seen in the database
      emrSystem: facility?.clinicalExpectations?.emrSystem ||
        (listing?.facilityDetails ? listing.facilityDetails.clinicalExpectations?.emrSystem : undefined) ||
        listing?.clinicalExpectations?.emrSystem ||
        facility?.clinical?.emrSystem ||
        listing?.clinical?.emrSystem ||
        listing?.clinicalRequirements?.emrSystem ||
        facility.clinicalRequirements?.emrSystem ||
        listing.emrSystem ||
        facility.emrSystem ||
        '',
      // Include skillsRequired from clinical object
      skillsRequired: listing.clinical?.skillsRequired || listing.clinicalExpectations?.skillsRequired || {}
    },

    // Also add clinical object directly for components that check this path
    clinical: {
      requiredCaseTypes: Array.isArray(listing.clinical?.requiredCaseTypes) ? listing.clinical.requiredCaseTypes :
        Array.isArray(listing.clinicalRequirements?.requiredCaseTypes) ? listing.clinicalRequirements.requiredCaseTypes :
          [],
      stateLicenseRequired: listing.clinical?.stateLicenseRequired === true,
      emrSystem: listing?.clinical?.emrSystem || '',
      skillsRequired: listing.clinical?.skillsRequired || {},
      requiredCertifications: Array.isArray(listing.clinical?.requiredCertifications) ? listing.clinical.requiredCertifications : [],
      careTeamModel: Array.isArray(listing.clinical?.careTeamModel) ? listing.clinical.careTeamModel : []
    },

    // Additional fields needed for complete display
    website: listing.website || facility.website || '',
    // Original/apply URLs use several names across internal feeds and the
    // normalized external catalog. Collapse them here so every card host hands
    // the shared details modal the same field.
    sourceUrl: listing.sourceUrl || listing.originalListingUrl || listing.originalUrl ||
      listing.listingUrl || listing.url || listing.applyUrl || listing.inquireLink || '',
    url: listing.url || '',
    applyUrl: listing.applyUrl || '',
    inquireLink: listing.inquireLink || '',
    includesHousing: listing.compensation?.includesHousing ?? listing.includesHousing ?? null,
    includesTravel: listing.compensation?.includesTravel ?? listing.includesTravel ?? null,
    malpracticeCovered: listing.compensation?.malpracticeCovered ?? listing.malpracticeCovered ?? null,

    // Share link fields (for anonymous swipe experience)
    shareUrl: listing.shareUrl || null,
    shareToken: listing.shareToken || null,

    // Source type indicator (internal = RxRooster, gaswork = external)
    // Used to display visual badge on listing cards
    sourceType: listing._listingType || listing.sourceType || 'internal',
    source: listing.source || '',

    // D1.11: server-authored trust badges (label and tooltip included). This
    // object is a WHITELIST, so a badge array nobody names here is dropped
    // silently and every card face below renders no badges at all while
    // every test stays green.
    trustBadges: Array.isArray(listing.trustBadges)
      ? listing.trustBadges
      : undefined,

    // Honest market band for the unified "Competitive Rate" badge. Present only
    // when the rate is not shown (comp-blind or blind) AND the backend resolved a
    // band; absent otherwise. Display context, never this listing's own offer.
    marketBand: listing.marketBand || undefined,

    // Provider type (CRNA / Anesthesiologist / CAA) for market-band copy and
    // downstream display. `section` is the legacy alias kept for existing cards.
    // Blank when the listing states neither: a card never invents a credential.
    providerType: listing.providerType || listing.section || '',

    // GasWork-specific fields for ExternalListingFront/Back components
    // These are passed through directly from the backend
    section: listing.section || listing.providerType || '',
    briefDescription: listing.briefDescription || listing.title || '',
    fullDescription: listing.fullDescription || listing.description || '',
    duration: listing.duration || listing.assignment?.duration || '',
    datePosted: listing.datePosted || listing.createdAt || null,
    priorityLevel: listing.priorityLevel || 'General',
    minIncome: listing.minIncome || 0,
    maxIncome: listing.maxIncome || 0,
    isHourlyRate: listing.isHourlyRate === true,
    annualizedMinIncome: listing.annualizedMinIncome || 0,
    annualizedMaxIncome: listing.annualizedMaxIncome || 0,

    // Match metadata from backend
    _matchScore: listing._matchScore,
    licenseRelevance: listing.licenseRelevance,
    licenseRequirements: listing.licenseRequirements,
    licenseRequirementsState: listing.licenseRequirementsState,
    _distance: listing._distance,
    _isBlindListing: listing._isBlindListing,

    // Standardization metadata (for external listings with pay data)
    _isStandardized: listing._isStandardized || false,
    _hasPayData: listing._hasPayData || false,

    // Contact info availability flags (for external listings)
    // Derive from backend contactEmail/contactPhone/contactName fields
    _hasPhone: listing._hasPhone || !!(listing.contactPhone),
    _hasEmail: listing._hasEmail || !!(listing.contactEmail),
    _hasName: listing._hasName || !!(listing.contactName),

    // Raw contact info fields from backend
    contactName: listing.contactName || null,
    contactEmail: listing.contactEmail || null,
    contactPhone: listing.contactPhone || null,
    hasContactInfo: listing.hasContactInfo || !!(listing.contactName || listing.contactEmail || listing.contactPhone),

    // Original data accessible for debugging
    _original: {
      input: data,
      listing,
      facility
    }
  };
};

/**
 * Transform a match-feed item (facilities[i] from GET /listings/matches) into
 * the `profile` shape FlippableListingCard expects. This is the exact transform
 * the Discover swipe deck (FacilityListingsSwipe) applies before rendering the
 * card; extracted here so the provider matches deck (/pm) can render the
 * IDENTICAL card from the same feed. Kept behavior-for-behavior with the
 * Discover copy (which still has its own local version pending a broader dedup).
 * @param {Object} facility - A match-feed listing/facility item
 * @param {Number|null} yearsOfExperience - Optional provider YOE for comp tiering
 * @returns {Object} Transformed profile for FlippableListingCard
 */
export const transformFacilityData = (facility, yearsOfExperience = null) => {
  if (!facility) return {};

  // External listings (_listingType 'external'/'gaswork', or a raw `section`
  // with no nested facility) go through the gaswork-shape transform.
  const isExternalListing =
    facility._listingType === 'external' ||
    facility._listingType === 'gaswork' ||
    (facility.section && !facility.facility);

  if (isExternalListing) {
    const { transformGasWorkListingToRxFormat } = require('./gasworkTransformUtils');
    const transformedData = transformGasWorkListingToRxFormat(facility);
    transformedData.sourceType = 'external';
    transformedData._original = facility;
    return transformedData;
  }

  // Internal: the item is the listing, with the facility nested under `facility`.
  const listing = facility.listing || facility;
  const actualFacility = listing.facility || facility.facility || {};
  const combinedData = { ...listing, facility: actualFacility };
  const transformedData = transformListingData(combinedData, yearsOfExperience);
  transformedData._original = facility;
  transformedData.sourceType = facility._listingType || 'internal';

  if (facility.facilityType || listing.facilityType || facility.type || listing.type) {
    const type = getFacilityTypeDisplayName(
      facility.facilityType || listing.facilityType || facility.type || listing.type,
    );
    transformedData.facilityName = `${facility.name || 'Facility Name'} - ${type}`;
  }

  return transformedData;
};
